/**
 * @file ExplosiveTrigger component: proximity-detonated area blast (issue #5).
 *
 * Where {@link ./Explosive.js Explosive} fires *after* an entity dies, this
 * component describes a blast the `enemy_suicide_bomber` triggers *before*
 * death — by touching the player or closing inside `explosion_trigger_distance`
 * — and, optionally, on death too (`explode_on_death`). It carries the full
 * blast description (falloff, force, line-of-sight, faction filters) plus the
 * one-shot flags that guarantee a bomber detonates exactly once.
 *
 * Like every component it is pure data: {@link ../systems/EnemyAISystem.js}
 * owns the fuse countdown and the damage application.
 */

import { Component } from '../ecs/Component.js';
import { nonNegative, positive, bool, warnParam } from '../utils/Validate.js';

export class ExplosiveTrigger extends Component {
  /**
   * @param {object} [options] Blast setup; every field validates with a fallback.
   * @param {string} [options.name] Owner label used in validation warnings.
   * @param {number} [options.triggerDistance] Proximity that arms detonation.
   * @param {number} [options.fuseSeconds] Delay from trigger to blast.
   * @param {number} [options.deathFuseSeconds] Delay when detonating on death.
   * @param {boolean} [options.explodeOnDeath] Blow up if killed before contact.
   * @param {number} [options.damage] Damage at the centre.
   * @param {number} [options.radius] Blast radius.
   * @param {boolean} [options.falloffEnabled] Scale damage down with distance.
   * @param {number} [options.minimumDamage] Damage at the rim when falloff is on.
   * @param {number} [options.force] Knockback impulse applied to victims.
   * @param {boolean} [options.requiresLineOfSight] Walls shield victims when true.
   * @param {boolean} [options.affectsPlayer] Whether the blast hurts the player.
   * @param {boolean} [options.affectsEnemies] Whether it hurts other enemies (chains).
   * @param {boolean} [options.affectsEnvironment] Whether it damages destructibles.
   */
  constructor(options = {}) {
    super();
    const who = options.name ?? 'ExplosiveTrigger';

    // --- Activation ------------------------------------------------------
    /** @type {number} Distance from bomber centre to player that arms the blast. */
    this.triggerDistance = nonNegative(who, 'explosion_trigger_distance', options.triggerDistance, 1.4);
    /** @type {number} Seconds between arming and detonation (0 = instant). */
    this.fuseSeconds = nonNegative(who, 'explosion_fuse_seconds', options.fuseSeconds, 0.35);
    /** @type {number} Fuse used when detonation is caused by death instead. */
    this.deathFuseSeconds = nonNegative(who, 'death_explosion_fuse_seconds', options.deathFuseSeconds, 0);
    /** @type {boolean} If true, dying before contact still detonates (chains). */
    this.explodeOnDeath = bool(who, 'explode_on_death', options.explodeOnDeath, true);

    // --- Blast -----------------------------------------------------------
    /** @type {number} Damage at the centre; the maximum. */
    this.damage = nonNegative(who, 'explosion_damage', options.damage, 40);
    /** @type {number} Blast radius in world units; must be > 0. */
    this.radius = positive(who, 'explosion_radius', options.radius, 5);
    /** @type {boolean} Whether damage tapers toward the rim. */
    this.falloffEnabled = bool(who, 'explosion_damage_falloff_enabled', options.falloffEnabled, true);
    /**
     * Damage applied at the rim when falloff is on. Clamped to never exceed
     * {@link damage} (spec: minimum may not be greater than the centre damage).
     * @type {number}
     */
    this.minimumDamage = nonNegative(who, 'explosion_minimum_damage', options.minimumDamage, 0);
    if (this.minimumDamage > this.damage) {
      // Spec: minimum may not exceed centre damage. Clamp and warn once.
      // (nonNegative alone cannot express this cross-field rule — 50 is still >= 0.)
      warnParam(who, 'explosion_minimum_damage', options.minimumDamage, this.damage, 'must be <= explosion_damage');
      this.minimumDamage = this.damage;
    }
    /** @type {number} Radial knockback impulse; 0 disables push. */
    this.force = nonNegative(who, 'explosion_force', options.force, 14);
    /** @type {boolean} If true, walls between centre and victim block damage. */
    this.requiresLineOfSight = bool(who, 'explosion_requires_line_of_sight', options.requiresLineOfSight, false);

    // --- Faction filters -------------------------------------------------
    /** @type {boolean} */
    this.affectsPlayer = bool(who, 'explosion_affects_player', options.affectsPlayer, true);
    /** @type {boolean} */
    this.affectsEnemies = bool(who, 'explosion_affects_enemies', options.affectsEnemies, true);
    /** @type {boolean} */
    this.affectsEnvironment = bool(who, 'explosion_affects_environment', options.affectsEnvironment, false);

    // --- One-shot runtime flags (per instance; never shared) -------------
    /** @type {boolean} Set once the fuse starts; blocks re-arming. */
    this.hasStartedExploding = false;
    /** @type {boolean} Set once damage is applied; blocks a second blast. */
    this.hasAppliedDamage = false;
    /** @type {number} Seconds left on the running fuse. */
    this.fuseRemaining = 0;
  }
}
